
import { GlobalContext, IModalFormElement } from 'components/GlobalContext';
import { useContext, useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import IHealthRiskAssessment from 'models/sql/IHealthRiskAssessment';
import IEquipment from 'models/sql/IEquipment';
import { formatDate } from 'utilities/StringUtility';
import ISalesRep from 'models/sql/ISalesRep';
import MiscService from 'services/MiscService';
import IOpportunity from 'models/sql/IOpportunity';
import useBreakpoint from 'utilities/UseBreakpoint';
import './styles/RepDashboard.scss';
import { getStatus, getStatusDate } from 'utilities/EquipmentUtility';
import { getBaseDomain } from 'utilities/MiscUtilities';
import QRCodeReader from 'components/QRCodeReader';

interface IForm {
    formName: string;
    url: string;
    type: string;
    dynamic: boolean;
    fullUrl: string;
};

const RepDashboardIndex = () => {
    const globalContext = useContext(GlobalContext);
    const globalContextRef = useRef(globalContext);
    globalContextRef.current = globalContext;
    const [opportunities, setOpportunities] = useState<IOpportunity[]>([]);
    const [airCards, setAirCards] = useState<IEquipment[]>([]);
    const [demos, setDemos] = useState<IEquipment[]>([]);
    const [hras, setHras] = useState<IHealthRiskAssessment[]>([]);
    const [rep, setRep] = useState<ISalesRep>();
    const [showLoadError, setShowLoadError] = useState(false);
    const [loading, setLoading] = useState(false);
    const [tabShowing, setTabShowing] = useState("Opportunities");
    const breakpoint = useBreakpoint();
    const [lastBreakpoint, setLastBreakpoint] = useState(breakpoint);
    const [aircardTaggingShowing, setAircardTaggingShowing] = useState(false);
    const [aircardTaggingSerials, setAircardTaggingSerials] = useState("");
    const aircardTaggingSerialsRef = useRef(aircardTaggingSerials);
    aircardTaggingSerialsRef.current = aircardTaggingSerials;
    const [submittingAirCards, setSubmittingAirCards] = useState(false);

    useEffect(() => {
        (async () => {
            setLoading(true);

            try {
                const accessToken = await globalContext.getAccessTokenAsync();
                const result = await MiscService.GetRepPortalDataAsync(accessToken);

                if (result) {
                    setRep(result.rep);
                    setOpportunities(result.opportunities);
                    setAirCards(result.airCards);
                    setDemos(result.demos);
                    setHras(result.hras);
                }
            }
            catch (e) {
                setShowLoadError(true);
            }

            setLoading(false);
        })();
    }, []);

    useEffect(() => {
        if ((lastBreakpoint != "lg" && lastBreakpoint != "xl") &&
            (breakpoint === "lg" || breakpoint === "xl")) {
            setTabShowing("Opportunities");
        }
        setLastBreakpoint(breakpoint);
    }, [breakpoint]);

    if (showLoadError) {
        return (
            <div className="error-message">
                <h2>There seems to be an error with your access rights, please notify your contact at TFTHealth for help.</h2>
            </div>
        );
    }

    const calcScore = (hra: IHealthRiskAssessment) => {
        const total = (
            (hra?.stationCommonAreasScore || 0) +
            (hra?.stationLocationFactorScore || 0) +
            (hra?.stationPpegearScore || 0) +
            (hra?.incidentEquipmentHandlingScore || 0) +
            (hra?.incidentGrossDeconScore || 0) +
            (hra?.incidentProtectiveEquipmentScore || 0) +
            (hra?.apparatusPpegearScore || 0) +
            (hra?.apparatusVehicleDeconScore || 0) +
            (hra?.apparatusVehicleFactorsScore || 0) +
            (hra?.teamHealthWellnessScore || 0) +
            (hra?.teamPovuseScore || 0) +
            (hra?.teamRiskAwarenessScore || 0));

        return (
            Math.ceil((total / ((12 * 3) * 3)) * 100) + "%"
        );
    };

    const tagAirCards = async () => {
        if (rep && rep.code) {
            setAircardTaggingSerials("");
            setAircardTaggingShowing(true);
        }
    };

    const fastFollowup = () => {
        window.open(`/Public/FastFollowup?r=${rep?.code}`, "_blank");
    };

    const hideAircardTaggingModal = () => {
        setAircardTaggingShowing(false);
    };

    const onEquipmentScanSuccess = (decodedText: string, decodedResult: any) => {
        if (decodedText) {
            const serialPortion = decodedText.split("/").slice(-1)[0].toLowerCase();
            if (aircardTaggingSerialsRef.current.indexOf(serialPortion) === -1) {
                setAircardTaggingSerials(aircardTaggingSerialsRef.current.trim() + "," + serialPortion);
            }
        }
    };

    const completeAirCardTaggingAsync = async () => {
        if (rep && rep.code) {
            setSubmittingAirCards(true);
            const accessToken = await globalContext.getAccessTokenAsync();
            await MiscService.tagAirCardsAsync(accessToken, rep.code, aircardTaggingSerials);
            setSubmittingAirCards(false);
            hideAircardTaggingModal();
        }
    };

    const hraInvite = () => {
        const inviteInfo = {
            formName: "Health Risk Assessment (North America)",
            url: "/public/hra",
            type: "hra",
            dynamic: true,
            fullUrl: `${window.location.protocol}//${getBaseDomain()}/public/hra`
        };

        buildDynamicUrl(
            inviteInfo,
            async (url, extraData) => {
                var emailAddress = extraData?.[0].value;

                if (emailAddress) {
                    const accessToken = await globalContext.getAccessTokenAsync();

                    await MiscService.emailFormLinkAsync(
                        accessToken,
                        emailAddress,
                        `${window.location.protocol}//${getBaseDomain()}` + url,
                        inviteInfo.formName,
                        rep?.fullName,
                        rep?.email);

                    window.alert("A link to this form has been emailed to " + emailAddress);
                }
            },
            [
                {
                    key: "email",
                    type: "text",
                    title: `Email Address to send the link to?`,
                    value: ""
                }
            ]);
    }

    const buildDynamicUrl = (form: IForm, callback: (url: string, extraData?: any[]) => void, customFormElements?: IModalFormElement[]) => {
        var formElements: IModalFormElement[] = [];

        switch (form.type) {
            case "demo":
                break;
            case "hra":
                formElements = [
                    {
                        key: "id",
                        title: "Source",
                        type: "text",
                        required: false,
                        value: ""
                    }
                ];
                break;
            case "register":
                formElements = [
                    {
                        key: "serial",
                        title: "Equipment Serial #",
                        type: "text",
                        required: true,
                        value: ""
                    }
                ];
                break;
            case "aircard":
                formElements = [
                    {
                        key: "serial",
                        title: "Equipment Serial #",
                        type: "text",
                        required: true,
                        value: ""
                    }
                ];
                break;
        }

        if (customFormElements) {
            formElements = [...formElements, ...customFormElements];
        }

        globalContext.showModal({
            content:
                <></>,
            title: "Build Link",
            formElements: formElements,
            okCallback:
                async (data) => {
                    var url = form.url;
                    switch (form.type) {
                        case "demo":
                            url += `?r=${rep?.code}`;
                            break;
                        case "hra":
                            url += `?id=${data?.[0].value}&r=${rep?.code}`;
                            break;
                        case "register":
                            url += `?serial=${data?.[0].value}`;
                            break;
                        case "aircard":
                            url += `?serial=${data?.[0].value}`;
                            break;
                    }
                    var extraData = data?.slice((data?.length ?? 0) - (customFormElements?.length ?? 0), (data?.length ?? 0) + (customFormElements?.length ?? 0));
                    callback(url, extraData);
                },
            cancelCallback: () => { }
        });
    };

    if (loading) {
        return (
            <div className="loading-display">
                <div className="content">
                    <h2>
                        <Spinner animation="border" role="status"></Spinner>
                        &nbsp;Loading...
                    </h2>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="ts-container">
                <div className="mb-07">
                    <div className="ts-header gap-3 mt-5 mb-05">
                        <div className="d-flex justify-content-between">
                            <h1 className="ts-fs-30 fw-semibold mb-0">
                                Dealer Dashboard
                            </h1>
                            <div className="d-none d-lg-flex gap-03">
                                {/* 
                                <button
                                    className="ts-btn ts-btn-primary-1 d-flex align-items-center gap-2 ts-fs-14"
                                    id="shareReportButton">
                                    <img
                                        width="19"
                                        src="/Images/Dashboard/icons/share-05.svg"
                                        alt="..." />
                                    Share
                                </button>
                                */}
                            </div>
                        </div>
                    </div>

                    <ul
                        className="nav nav-tabs"
                        id="tabs"
                        role="tablist">
                        <li
                            className="nav-item"
                            role="presentation">
                            <button
                                className={`nav-link ${tabShowing === "Opportunities" ? "active" : ""}`}
                                id="Opportunities-tab"
                                type="button"
                                role="tab"
                                aria-controls="Opportunities-tab-pane"
                                onClick={() => setTabShowing("Opportunities")}>
                                Opportunities
                            </button>
                        </li>
                        <li
                            className="nav-item"
                            role="presentation">
                            <button
                                className={`nav-link ${tabShowing === "AirCards" ? "active" : ""}`}
                                id="AirCards-tab"
                                type="button"
                                role="tab"
                                aria-controls="AirCards-tab-pane"
                                onClick={() => setTabShowing("AirCards")}>
                                AirCards
                            </button>
                        </li>
                        <li
                            className="nav-item"
                            role="presentation">
                            <button
                                className={`nav-link ${tabShowing === "Hras" ? "active" : ""}`}
                                id="hras-tab"
                                type="button"
                                role="tab"
                                aria-controls="Hras-tab-pane"
                                onClick={() => setTabShowing("Hras")}>
                                HRAs
                            </button>
                        </li>
                        <li
                            className="nav-item"
                            role="presentation">
                            <button
                                className={`nav-link ${tabShowing === "Demos" ? "active" : ""}`}
                                id="demos-tab"
                                type="button"
                                role="tab"
                                aria-controls="Hras-tab-pane"
                                onClick={() => setTabShowing("Demos")}>
                                Demos
                            </button>
                        </li>
                        <li
                            className="nav-item d-lg-none"
                            role="presentation">
                            <button
                                className={`nav-link ${tabShowing === "Account" ? "active" : ""}`}
                                id="Account-tab"
                                type="button"
                                role="tab"
                                aria-controls="Account-tab-pane"
                                onClick={() => setTabShowing("Account")}>
                                Account-Details
                            </button>
                        </li>
                    </ul>

                    <hr className="d-none d-lg-block" />
                </div>
            </div>
            <main>
                <div className="ts-container">
                    <div
                        className="ts-main-layout tab-content mb-5"
                        id="tabContent">
                        <div className={`tab-pane fade ${(tabShowing === "Account" || breakpoint === "lg" || breakpoint === "xl") ? "show active" : ""}`}
                            id="Account-tab-pane"
                            role="tabpanel"
                            aria-labelledby="Account-tab"
                            tabIndex={0}>
                            <div className="ts-profile-card ts-bg-white" style={{ "padding": "1.25rem clamp(1rem, 2vw, 1.5rem)" }}>
                                <div className="mb-05 mb-lg-06 fw-medium">
                                    <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                        Company
                                    </p>
                                    <p className="ts-text-gray-900 mb-0">{rep?.dealer?.dealerName || rep?.company}</p>
                                </div>

                                <div className="mb-05 mb-lg-06 fw-medium">
                                    <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                        Rep Name
                                    </p>
                                    <p className="ts-text-gray-900 mb-0">{rep?.fullName}</p>
                                </div>

                                <div className="mb-05 mb-lg-06 fw-medium">
                                    <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                        Rep Email
                                    </p>
                                    <p className="ts-text-gray-900 mb-0">{rep?.email}</p>
                                </div>
                                {
                                    rep?.phone &&
                                    <div className="mb-05 mb-lg-06 fw-medium">
                                        <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                            Rep Phone
                                        </p>
                                        <p className="ts-text-gray-900 mb-0">{rep?.phone}</p>
                                    </div>
                                }

                                <div className="mb-05 mb-lg-06 fw-medium">
                                    <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                        TFT Rep Code
                                    </p>
                                    <p className="ts-text-gray-900 mb-0">{rep?.code}</p>
                                </div>

                                <div className="mb-05 mb-lg-06 fw-medium">
                                    {/* 
                                    <button
                                        className="ts-btn ts-btn-primary-1 d-flex align-items-center gap-2 ts-fs-14">
                                        Edit
                                    </button>
                                    */}
                                    {
                                        (rep?.canRequestDemo === true || rep?.dealer?.canRequestDemo === true) &&
                                        rep?.canRequestDemo !== false &&
                                        <a
                                            href={`/Public/DemoRequest?r=${rep.code}`}
                                            target="_blank">
                                            <button
                                                className="ts-btn ts-btn-primary-1 d-flex align-items-center gap-2 ts-fs-14 mb-2"
                                                style={{ width: "100%" }}>
                                                Demo Request
                                            </button>
                                        </a>
                                    }
                                    <button
                                        className="ts-btn ts-btn-primary-1 d-flex align-items-center gap-2 ts-fs-14 mb-2"
                                        style={{ width: "100%" }}
                                        onClick={hraInvite}>
                                        Send HRA Invite
                                    </button>
                                    <button
                                        className="ts-btn ts-btn-primary-1 d-flex align-items-center gap-2 ts-fs-14 mb-2"
                                        style={{ width: "100%" }}
                                        onClick={tagAirCards}>
                                        Tag AirCards
                                    </button>
                                    {
                                        rep?.code == "0000" && // todo: remove
                                        <button
                                            className="ts-btn ts-btn-primary-1 d-flex align-items-center gap-2 ts-fs-14"
                                            style={{ width: "100%" }}
                                            onClick={fastFollowup}>
                                            Fast Follow-Up
                                        </button>
                                    }
                                </div>
                            </div>
                        </div>
                        <div className={`tab-pane fade ${tabShowing === "Opportunities" ? "show active" : ""}`}
                            id="Opportunities-tab-pane"
                            role="tabpanel"
                            aria-labelledby="Opportunities-tab"
                            tabIndex={0}>
                            <div>
                                {/* 
                                <div className="ts-custom-card mb-05 mb-lg-06">
                                    <div className="ts-custom-card__header">
                                        <h1 className="ts-fs-18 ts-text-gray-900 fw-semibold mb-0">
                                            HRA & AirCard Stats
                                        </h1>
                                    </div>
                                    <div className="ts-custom-card__body">
                                        <div className="d-flex flex-column flex-sm-row flex-wrap gap-20 gap-sm-5">
                                            Nothing here yet
                                        </div>
                                    </div>
                                </div>
                                */}
                                {
                                    opportunities.length > 0 &&
                                    opportunities.map((opportunity, index) =>
                                        <>
                                            {
                                                opportunity.healthRiskAssessment &&
                                                <div className="ts-custom-card mb-05 mb-lg-06">
                                                    <div className="ts-custom-card__header" style={{ "padding": "0.8rem" }}>
                                                        <h3 className="ts-fs-18 ts-text-gray-900 fw-semibold mb-0">
                                                            {opportunity.fireDepartment?.fireDeptName || opportunity.departmentName || "N/A"}, {opportunity.fireDepartment?.hqstate || "N/A"}
                                                        </h3>
                                                    </div>
                                                    <div className="ts-custom-card__body" style={{ "padding": "0.8rem" }}>
                                                        <div className="d-flex flex-column flex-sm-row flex-wrap gap-4">
                                                            <div className="fw-medium">
                                                                <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                                                    Created Date
                                                                </p>
                                                                <p className="ts-text-gray-900 mb-0">{formatDate(opportunity.created)}</p>
                                                            </div>
                                                            <div className="fw-medium">
                                                                <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                                                    Source
                                                                </p>
                                                                <p className="ts-text-gray-900 mb-0">HRA</p>
                                                            </div>
                                                            {
                                                                rep?.salesRepId != opportunity.salesRepId &&
                                                                <div className="fw-medium">
                                                                    <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                                                        Rep
                                                                    </p>
                                                                    <p className="ts-text-gray-900 mb-0">{opportunity.salesRep?.fullName}</p>
                                                                </div>
                                                            }
                                                            <div className="fw-medium">
                                                                <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                                                    Contact
                                                                </p>
                                                                <p className="ts-text-gray-900 mb-0">{opportunity.healthRiskAssessment.userName}</p>
                                                            </div>
                                                            <div className="fw-medium">
                                                                <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                                                    Email
                                                                </p>
                                                                <p className="ts-text-gray-900 mb-0">
                                                                    <a href={`mailto:${opportunity.healthRiskAssessment.userEmail}`}>
                                                                        {opportunity.healthRiskAssessment.userEmail}
                                                                    </a>
                                                                </p>
                                                            </div>
                                                            <div className="fw-medium">
                                                                <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                                                    Report Number
                                                                </p>
                                                                <p className="ts-text-gray-900 mb-0">
                                                                    <a href={`/public/hrareport?code=${opportunity.healthRiskAssessment.code}`} target="_blank">
                                                                        {opportunity.healthRiskAssessment.code}
                                                                    </a>
                                                                </p>
                                                            </div>
                                                            {/*
                                                            <div className="fw-medium">
                                                                <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                                                    Status Date
                                                                </p>
                                                                <p className="ts-text-gray-900 mb-0">???</p>
                                                            </div>
                                                            <div className="fw-medium">
                                                                <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                                                    Status
                                                                </p>
                                                                <p className="ts-text-gray-900 mb-0">???</p>
                                                            </div>
                                                            */}
                                                            <div className="fw-medium">
                                                                <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                                                    Score
                                                                </p>
                                                                <p className="ts-text-gray-900 mb-0">{calcScore(opportunity.healthRiskAssessment)}</p>
                                                            </div>
                                                            <div className="fw-medium">
                                                                <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                                                    Budget Quote
                                                                </p>
                                                                <p className="ts-text-gray-900 mb-0">
                                                                    <a href={`/Public/BudgetaryQuote?code=${opportunity.code}`} target="_blank">
                                                                        ${opportunity.quotedTotalValue?.toFixed(2) ?? "0.00"}
                                                                    </a>
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            }
                                            {
                                                opportunity.airCard &&
                                                <div className="ts-custom-card mb-05 mb-lg-06">
                                                    <div className="ts-custom-card__header" style={{ "padding": "0.8rem" }}>
                                                        <h3 className="ts-fs-18 ts-text-gray-900 fw-semibold mb-0">
                                                            {opportunity.fireDepartment?.fireDeptName || opportunity.departmentName || "N/A"}, {opportunity.fireDepartment?.hqstate || "N/A"}
                                                        </h3>
                                                    </div>
                                                    <div className="ts-custom-card__body" style={{ "padding": "0.8rem" }}>
                                                        <div className="d-flex flex-column flex-sm-row flex-wrap gap-4">
                                                            <div className="fw-medium">
                                                                <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                                                    Created Date
                                                                </p>
                                                                <p className="ts-text-gray-900 mb-0">{formatDate(opportunity.created)}</p>
                                                            </div>
                                                            <div className="fw-medium">
                                                                <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                                                    Source
                                                                </p>
                                                                <p className="ts-text-gray-900 mb-0">AirCard</p>
                                                            </div>
                                                            {
                                                                rep?.salesRepId != opportunity.salesRepId &&
                                                                <div className="fw-medium">
                                                                    <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                                                        Rep
                                                                    </p>
                                                                    <p className="ts-text-gray-900 mb-0">{opportunity.salesRep?.fullName}</p>
                                                                </div>
                                                            }
                                                            <div className="fw-medium">
                                                                <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                                                    Contact
                                                                </p>
                                                                <p className="ts-text-gray-900 mb-0">{opportunity.airCard.registrationName}</p>
                                                            </div>
                                                            <div className="fw-medium">
                                                                <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                                                    Email
                                                                </p>
                                                                <p className="ts-text-gray-900 mb-0">
                                                                    <a href={`mailto:${opportunity.airCard.registrationEmail}`}>
                                                                        {opportunity.airCard.registrationEmail}
                                                                    </a>
                                                                </p>
                                                            </div>
                                                            <div className="fw-medium">
                                                                <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                                                    Report Number
                                                                </p>
                                                                <p className="ts-text-gray-900 mb-0">
                                                                    <a href={`/public/AircardReport?serial=${opportunity.airCard.serialNumber}&postal=${opportunity.airCard.registrationPostalCode}`} target="_blank">
                                                                        {opportunity.airCard.serialNumber?.toUpperCase()}
                                                                    </a>
                                                                </p>
                                                            </div>
                                                            <div className="fw-medium">
                                                                <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                                                    Status Date
                                                                </p>
                                                                <p className="ts-text-gray-900 mb-0">{getStatusDate(opportunity.airCard)}</p>
                                                            </div>
                                                            <div className="fw-medium">
                                                                <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                                                    Status
                                                                </p>
                                                                <p className="ts-text-gray-900 mb-0">{getStatus(opportunity.airCard)}</p>
                                                            </div>
                                                            <div className="fw-medium">
                                                                <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                                                    Score
                                                                </p>
                                                                <p className="ts-text-gray-900 mb-0">{opportunity.airCard.score}</p>
                                                            </div>
                                                            <div className="fw-medium">
                                                                <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                                                    Budget Quote
                                                                </p>
                                                                <p className="ts-text-gray-900 mb-0">
                                                                    <a href={`/Public/BudgetaryQuote?code=${opportunity.code}`} target="_blank">
                                                                        ${opportunity.quotedTotalValue?.toFixed(2) ?? "0.00"}
                                                                    </a>
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            }
                                            {
                                                !opportunity.airCard && !opportunity.healthRiskAssessment &&
                                                <div className="ts-custom-card mb-05 mb-lg-06">
                                                    <div className="ts-custom-card__header" style={{ "padding": "0.8rem" }}>
                                                        <h3 className="ts-fs-18 ts-text-gray-900 fw-semibold mb-0">
                                                            {opportunity.fireDepartment?.fireDeptName || opportunity.departmentName || "N/A"}, {opportunity.fireDepartment?.hqstate || "N/A"}
                                                        </h3>
                                                    </div>
                                                    <div className="ts-custom-card__body" style={{ "padding": "0.8rem" }}>
                                                        <div className="d-flex flex-column flex-sm-row flex-wrap gap-4">
                                                            <div className="fw-medium">
                                                                <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                                                    Created Date
                                                                </p>
                                                                <p className="ts-text-gray-900 mb-0">{formatDate(opportunity.created)}</p>
                                                            </div>
                                                            <div className="fw-medium">
                                                                <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                                                    Source
                                                                </p>
                                                                <p className="ts-text-gray-900 mb-0">{opportunity.source || ""}</p>
                                                            </div>
                                                            {
                                                                rep?.salesRepId != opportunity.salesRepId &&
                                                                <div className="fw-medium">
                                                                    <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                                                        Rep
                                                                    </p>
                                                                    <p className="ts-text-gray-900 mb-0">{opportunity.salesRep?.fullName}</p>
                                                                </div>
                                                            }
                                                            <div className="fw-medium">
                                                                <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                                                    Contact
                                                                </p>
                                                                <p className="ts-text-gray-900 mb-0">{opportunity.pocprimaryName}</p>
                                                            </div>
                                                            <div className="fw-medium">
                                                                <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                                                    Email
                                                                </p>
                                                                <p className="ts-text-gray-900 mb-0">
                                                                    <a href={`mailto:${opportunity.pocprimaryEmail}`}>
                                                                        {opportunity.pocprimaryEmail}
                                                                    </a>
                                                                </p>
                                                            </div>
                                                            <div className="fw-medium">
                                                                <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                                                    Stage
                                                                </p>
                                                                <p className="ts-text-gray-900 mb-0">{opportunity.stage}</p>
                                                            </div>
                                                            <div className="fw-medium">
                                                                <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                                                    Budget Quote
                                                                </p>
                                                                <p className="ts-text-gray-900 mb-0">
                                                                    <a href={`/Public/BudgetaryQuote?code=${opportunity.code}`} target="_blank">
                                                                        ${opportunity.quotedTotalValue?.toFixed(2) ?? "0.00"}
                                                                    </a>
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            }
                                        </>

                                    )
                                }

                            </div>
                        </div>
                        <div className={`tab-pane fade ${tabShowing === "AirCards" ? "show active" : ""}`}
                            id="AirCards-tab-pane"
                            role="tabpanel"
                            aria-labelledby="AirCards-tab"
                            tabIndex={0}>
                            <div>
                                <div className="ts-custom-card mb-05 mb-lg-06">
                                    <div className="ts-custom-card__header" style={{ "padding": "0.8rem" }}>
                                        <h3 className="ts-fs-18 ts-text-gray-900 fw-semibold mb-0">
                                            AirCards
                                        </h3>
                                    </div>
                                    <div className="ts-custom-card__body" style={{ "padding": "0.8rem" }}>
                                        <div className="d-flex flex-column flex-sm-row flex-wrap gap-4">
                                            <h4>
                                                <strong>{airCards.map(a => getStatus(a)).filter(a => a != undefined).length}</strong> in status out of <strong>{airCards.length}</strong> total.
                                            </h4>
                                        </div>
                                    </div>
                                </div>
                                {
                                    airCards.length > 0 &&
                                    airCards
                                        .sort(
                                            (a, b) => {
                                                if ((a.salesRep?.fullName || "") > (b.salesRep?.fullName || "")) {
                                                    return 1;
                                                }
                                                else if ((a.salesRep?.fullName || "") > (b.salesRep?.fullName || "")) {
                                                    return -1;
                                                }
                                                else {
                                                    return 0;
                                                }
                                            })
                                        .map((aircard, index) =>
                                            <>
                                                {
                                                    aircard &&
                                                    <div className="ts-custom-card mb-05 mb-lg-06">
                                                        <div className="ts-custom-card__body">
                                                            <div className="d-flex flex-column flex-sm-row flex-wrap gap-20 gap-sm-5">
                                                                <div className="fw-medium">
                                                                    <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                                                        <strong>{aircard.salesRep?.fullName || "Not Tagged"} ({aircard.serialNumber?.toUpperCase()})</strong>
                                                                    </p>
                                                                    <p className="ts-text-gray-900 mb-0">Status: {getStatus(aircard) || "Unused"} {getStatus(aircard) != undefined ? "on " + getStatusDate(aircard) : ""}</p>
                                                                    {
                                                                        aircard.fireDepartment &&
                                                                        <p className="ts-text-gray-900 mb-0">Department: {aircard.fireDepartment.fireDeptName}</p>
                                                                    }
                                                                    {
                                                                        (aircard.registrationDate && aircard.outOfServiceDate) &&
                                                                        <>
                                                                            {
                                                                                aircard.score ?
                                                                                    <a href={`/public/AircardReport?serial=${aircard.serialNumber}&postal=${aircard.registrationPostalCode}`} target="_blank">
                                                                                        View Report
                                                                                    </a> :
                                                                                    <>Results Pending</>
                                                                            }
                                                                        </>
                                                                    }
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                }
                                            </>
                                        )
                                }
                            </div>
                        </div>
                        <div className={`tab-pane fade ${tabShowing === "Hras" ? "show active" : ""}`}
                            id="Hras-tab-pane"
                            role="tabpanel"
                            aria-labelledby="Hras-tab"
                            tabIndex={0}>
                            <div>
                                <div className="ts-custom-card mb-05 mb-lg-06">
                                    <div className="ts-custom-card__header" style={{ "padding": "0.8rem" }}>
                                        <h3 className="ts-fs-18 ts-text-gray-900 fw-semibold mb-0">
                                            HRAs
                                        </h3>
                                    </div>
                                    <div className="ts-custom-card__body" style={{ "padding": "0.8rem" }}>
                                        <div className="d-flex flex-column flex-sm-row flex-wrap gap-4">
                                            <h4>
                                                <strong>{hras.length}</strong> total.
                                            </h4>
                                        </div>
                                    </div>
                                </div>
                                {
                                    hras.length > 0 &&
                                    hras
                                        .sort(
                                            (a, b) => {
                                                if ((a.salesRep?.fullName || "") > (b.salesRep?.fullName || "")) {
                                                    return 1;
                                                }
                                                else if ((a.salesRep?.fullName || "") > (b.salesRep?.fullName || "")) {
                                                    return -1;
                                                }
                                                else {
                                                    return 0;
                                                }
                                            })
                                        .map((hra, index) =>
                                            <>
                                                {
                                                    hra &&
                                                    <div className="ts-custom-card mb-05 mb-lg-06">
                                                        <div className="ts-custom-card__body">
                                                            <div className="d-flex flex-column flex-sm-row flex-wrap gap-20 gap-sm-5">
                                                                <div className="fw-medium">
                                                                    <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                                                        <strong>{hra.salesRep?.fullName || "Not Tagged"} Score: {calcScore(hra)}</strong>
                                                                    </p>
                                                                    <p className="ts-text-gray-900 mb-0">Submitted: {formatDate(hra.submissionDate)}</p>
                                                                    {
                                                                        <p className="ts-text-gray-900 mb-0">Department: {hra.fireDepartment?.fireDeptName || hra.departmentName}</p>
                                                                    }
                                                                    <a href={`/public/hrareport?code=${hra.code}`} target="_blank">
                                                                        View Report
                                                                    </a>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                }
                                            </>
                                        )
                                }
                            </div>
                        </div>

                        <div className={`tab-pane fade ${tabShowing === "Demos" ? "show active" : ""}`}
                            id="Demos-tab-pane"
                            role="tabpanel"
                            aria-labelledby="Demos-tab"
                            tabIndex={0}>
                            <div>
                                <div className="ts-custom-card mb-05 mb-lg-06">
                                    <div className="ts-custom-card__header" style={{ "padding": "0.8rem" }}>
                                        <h3 className="ts-fs-18 ts-text-gray-900 fw-semibold mb-0">
                                            Demos
                                        </h3>
                                    </div>
                                    <div className="ts-custom-card__body" style={{ "padding": "0.8rem" }}>
                                        <div className="d-flex flex-column flex-sm-row flex-wrap gap-4">
                                            <h4>
                                                <strong>{demos.length}</strong> total.
                                            </h4>
                                        </div>
                                    </div>
                                </div>
                                {
                                    demos.length > 0 &&
                                    demos
                                        .sort(
                                            (a, b) => {
                                                if ((a.salesRep?.fullName || "") > (b.salesRep?.fullName || "")) {
                                                    return 1;
                                                }
                                                else if ((a.salesRep?.fullName || "") > (b.salesRep?.fullName || "")) {
                                                    return -1;
                                                }
                                                else {
                                                    return 0;
                                                }
                                            })
                                        .map((demo, index) =>
                                            <>
                                                {
                                                    demo &&
                                                    <div className="ts-custom-card mb-05 mb-lg-06">
                                                        <div className="ts-custom-card__body">
                                                            <div className="d-flex flex-column flex-sm-row flex-wrap gap-20 gap-sm-5">
                                                                <div className="fw-medium">
                                                                    <p className="ts-fs-14 ts-text-gray-500 mb-2">
                                                                        <strong>{demo.product?.name}</strong>
                                                                    </p>
                                                                    <p className="ts-text-gray-900 mb-0">
                                                                        Department: {demo.fireDepartment?.fireDeptName || demo.departmentName}<br />
                                                                        Demo Contact: {demo.contactTitle}<br />
                                                                        {demo.product?.name} ({demo.product?.partNumber}), qty 1<br />
                                                                        Requested: {formatDate(demo.demoRequestDate)}<br />
                                                                        Approved: {demo.demoApprovalDate ? formatDate(demo.demoApprovalDate) : "Awaiting approval"}<br />
                                                                        Shipped: {demo.shippedDate ? formatDate(demo.shippedDate) : "Not yet shipped"}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                }
                                            </>
                                        )
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </main >

            <Modal
                show={aircardTaggingShowing}
                id="aircardTaggingModal"
                onHide={hideAircardTaggingModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Tag AirCards</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {
                        submittingAirCards &&
                        <Row>
                            <Col className='text-center'>
                                <h2>Submitting Tags</h2>
                                <p>
                                    Please wait...
                                </p>
                                <Spinner animation="border" role="status"></Spinner>
                            </Col>
                        </Row>
                    }
                    {
                        !submittingAirCards &&
                        <>
                            <Row>
                                <Col>
                                    <QRCodeReader onScanSuccess={onEquipmentScanSuccess} />
                                </Col>
                            </Row>
                            <Row>
                                <Col>
                                    <Form.Group className="mb-3" >
                                        <Form.Label>AirCard Serial(s)</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={10}
                                            className="mt-3"
                                            placeholder="Serials"
                                            onChange={
                                                (e: React.ChangeEvent<HTMLInputElement>) => {
                                                    // use regex to replace all line breaks with commas
                                                    setAircardTaggingSerials((e.target.value ?? "").split("\n").join(","));
                                                }
                                            }
                                            value={(aircardTaggingSerials?.split(",") ?? []).join("\n")}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </>
                    }

                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={hideAircardTaggingModal}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        disabled={(aircardTaggingSerials?.split(",") || []).filter(e => !!e).length == 0}
                        onClick={completeAirCardTaggingAsync}>
                        Register {aircardTaggingSerials?.split(",").filter(e => !!e).length ?? 0} AirCards
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

export default RepDashboardIndex;